import { useState, FormEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthCard, PasswordInput } from '@/components/auth';
import { useAuthFlow } from '@/hooks/useAuthFlow';
import { useAuth } from '@/hooks/useAuth';
import { isValidEmail } from '@/hooks/usePasswordValidation';
import { Loader2, AlertCircle, Sparkles } from 'lucide-react';

export default function Login() {
  const [, setLocation] = useLocation();
  const { mockLogin, user } = useAuth();
  const { login, isSubmitting, error, clearError, setPendingEmail } = useAuthFlow();

  // Get redirect location from sessionStorage or default to home
  const from = sessionStorage.getItem('authRedirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    const result = await login(email, password);

    if (result.success) {
      // Clear redirect storage
      sessionStorage.removeItem('authRedirect');

      // Check onboarding status from the auth hook (loaded via Hub on signedIn)
      // Give it a brief moment for the async user record fetch to complete.
      setTimeout(() => {
        if (user && user.onboardingComplete === false) {
          setLocation('/onboarding/profile');
          return;
        }
        setLocation(from);
      }, 150);
    } else if (result.needsVerification) {
      // User hasn't verified email yet
      setPendingEmail(email);
      setLocation('/verify-email');
    }
  };

  const handleDemoLogin = () => {
    mockLogin();
    sessionStorage.removeItem('authRedirect');
    setLocation(from);
  };

  return (
    <AuthCard
      title="Welcome back"
      description="Sign in to continue to SunSquatcher"
      footer={
        <p>
          Don&apos;t have an account?{' '}
          <Link href="/signup" className="text-amber-600 hover:text-amber-700 font-medium">
            Sign up
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (validationErrors.email) {
                setValidationErrors(prev => ({ ...prev, email: undefined }));
              }
            }}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={isSubmitting}
            aria-invalid={!!validationErrors.email}
            aria-describedby={validationErrors.email ? 'email-error' : undefined}
            className={validationErrors.email ? 'border-red-500' : ''}
          />
          {validationErrors.email && (
            <p id="email-error" className="text-sm text-red-500" role="alert">{validationErrors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              href="/forgot-password"
              className="text-sm text-amber-600 hover:text-amber-700"
            >
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            label=""
            value={password}
            onChange={(value) => {
              setPassword(value);
              if (validationErrors.password) {
                setValidationErrors(prev => ({ ...prev, password: undefined }));
              }
            }}
            placeholder="Enter your password"
            error={validationErrors.password}
            disabled={isSubmitting}
            autoComplete="current-password"
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleDemoLogin}
          disabled={isSubmitting}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Continue with Demo Mode
        </Button>
      </form>
    </AuthCard>
  );
}
